import React, { FC, Fragment, useEffect, useState } from 'react';
import {
  BlueEqualToIcon,
  BlueGreaterThanIcon,
  BlueInBetweenIcon,
  BlueLessThanIcon,
  BlueTickWithBlueBorderIcon,
  CrossIcon,
  DownArrowIcon,
  EqualToIcon,
  EqualToIconWithGreyBG,
  GreaterThanIcon,
  GreaterThanIconWithGreyBG,
  InBetweenIcon,
  InBetweenIconWithGreyBG,
  LessThanIcon,
  LessThanIconWithGreyBG,
  PlusIcon,
  TickWithBlueBorderIcon
} from 'assets/icons';
import { AmountFilterOperation } from 'constants/common';
import { Popover, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import AmountFilterPopoverPanel from './AmountFilterPopoverPanel';

export interface AmountFilter {
  filterOperation: AmountFilterOperation;
  minAmount?: number;
  maxAmount?: number;
}

interface CustomAmountFilterProps {
  amountFilter: AmountFilter;
  setAmountFilter: (amountFilter: AmountFilter) => void;
  handleDropdownOpen?: () => void;
  handleRemoveFilter?: () => void;
  title?: string;
  isAmountFilter?: boolean;
}

export interface AmountFilterItem {
  key: AmountFilterOperation;
  value: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  SelectedIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  SummaryIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
}

export const amountFilters: AmountFilterItem[] = [
  {
    key: AmountFilterOperation.ALL,
    value: 'All',
    Icon: TickWithBlueBorderIcon,
    SelectedIcon: BlueTickWithBlueBorderIcon
  },
  {
    key: AmountFilterOperation.GT,
    value: 'Greater than',
    Icon: GreaterThanIcon,
    SelectedIcon: BlueGreaterThanIcon,
    SummaryIcon: GreaterThanIconWithGreyBG
  },
  {
    key: AmountFilterOperation.LT,
    value: 'Less than',
    Icon: LessThanIcon,
    SelectedIcon: BlueLessThanIcon,
    SummaryIcon: LessThanIconWithGreyBG
  },
  {
    key: AmountFilterOperation.BW,
    value: 'In between',
    Icon: InBetweenIcon,
    SelectedIcon: BlueInBetweenIcon,
    SummaryIcon: InBetweenIconWithGreyBG
  },
  {
    key: AmountFilterOperation.EQ,
    value: 'Equal to',
    Icon: EqualToIcon,
    SelectedIcon: BlueEqualToIcon,
    SummaryIcon: EqualToIconWithGreyBG
  }
];

const CustomAmountFilter: FC<CustomAmountFilterProps> = ({
  amountFilter,
  setAmountFilter,
  handleDropdownOpen,
  handleRemoveFilter,
  title,
  isAmountFilter = true
}) => {
  const { filterOperation, minAmount, maxAmount } = amountFilter ?? {};

  const [operation, setOperation] = useState<AmountFilterOperation>();
  const [startValue, setStartValue] = useState<number>();
  const [endValue, setEndValue] = useState<number>();

  useEffect(() => {
    setOperation(filterOperation);
    setStartValue(minAmount);
    setEndValue(maxAmount);
  }, [filterOperation, minAmount, maxAmount]);

  const getSecondaryText = () => {
    const item = amountFilters.find(filter => filter.key === filterOperation);
    const { key, SummaryIcon, value } = item ?? {};
    const prefix = isAmountFilter ? '$ ' : '';
    let filterText = '';
    if (key === AmountFilterOperation.BW) {
      filterText = `${value}: ${prefix}${minAmount}-${prefix}${maxAmount}`;
    } else {
      filterText = `${value}: ${prefix}${minAmount}`;
    }

    return (
      <div className="flex gap-1">
        {SummaryIcon && <SummaryIcon className="shrink-0" />}
        <div className="text-sbase font-semibold text-primaryBtn">{filterText}</div>
      </div>
    );
  };

  return (
    <div>
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={twMerge(
                `shrink-0 rounded-md border 
              bg-primary px-3 py-2 text-sbase font-semibold text-primaryText  
               outline-none transition-all  duration-150 ease-linear focus:outline-none 
              ${filterOperation === AmountFilterOperation.ALL ? ' border-dashed' : 'border-solid bg-secondaryBtn'} `
              )}
              id="amount-filter"
              style={{ borderColor: '#ACB5BD' }}
              type="button"
              onClick={() => handleDropdownOpen?.()}>
              <div className={'flex shrink-0 flex-row items-center'}>
                <div>
                  {filterOperation !== AmountFilterOperation.ALL ? (
                    <CrossIcon
                      onClick={e => {
                        e.stopPropagation();
                        setAmountFilter({
                          filterOperation: AmountFilterOperation.ALL,
                          maxAmount: null,
                          minAmount: null
                        });
                        handleRemoveFilter?.();
                      }}
                    />
                  ) : (
                    <PlusIcon />
                  )}
                </div>
                <div className={'flex shrink-0 gap-0.5 px-3 text-sbase'}>
                  {title || 'Amount'}
                  {filterOperation !== AmountFilterOperation.ALL && (
                    <div className="hidden gap-0.5 lg:flex">
                      <span> {' | '} </span>
                      {getSecondaryText()}
                    </div>
                  )}
                </div>
                <div>
                  <DownArrowIcon className={open ? 'rotate-180' : ''} />
                </div>
              </div>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute z-40 mt-0.5 min-w-[200px] bg-primary px-4 shadow-md sm:px-0">
                <div
                  className={
                    (open ? 'block ' : 'hidden ') + 'z-50  pt-7  list-none rounded-md border border-[#ACB5BD] w-fit'
                  }
                  style={{
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
                  }}>
                  <AmountFilterPopoverPanel
                    operation={operation}
                    setOperation={setOperation}
                    startValue={startValue}
                    setStartValue={setStartValue}
                    endValue={endValue}
                    setEndValue={setEndValue}
                    amountFilter={amountFilter}
                    setAmountFilter={setAmountFilter}
                    close={close}
                    title={title}
                    isAmountFilter={isAmountFilter}
                  />
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default CustomAmountFilter;
